import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { DialogId } from "./dialog_id.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { ImgID } from "../../image_loader/img_id.js";
import { LinkedSocialProvider } from "../dialog/account_providers_preview_state.js";

export class DisconnectAccountDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.DISCONNECT_ACCOUNT_DIALOG;

  private bg!: RoundedRectangle;
  private content_bg!: RoundedRectangle;

  constructor( private readonly provider: LinkedSocialProvider, private readonly on_confirm?: () => void )
  {
    const body_w_design = 990;
    const body_h_design = 776;

    const body_w = LayoutScaler.widthFromDesignAdaptive( body_w_design );
    const body_h = LayoutScaler.heightFromDesign( body_h_design );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createTitle();
    this.createContent();
    this.createActionButtons();
    this.createCloseIcon();
  }

  public name(): string { return "DisconnectAccountDialog"; }

  private getProviderIcon(): ImgID
  {
    switch ( this.provider )
    {
      case LinkedSocialProvider.Google:   return ImgID.GoogleIcon;
      case LinkedSocialProvider.Facebook: return ImgID.FacebookIcon;
      case LinkedSocialProvider.Apple:    return ImgID.AppleIcon;
    }
  }

  private getProviderDisplayName(): string
  {
    switch ( this.provider )
    {
      case LinkedSocialProvider.Google:   return "Google";
      case LinkedSocialProvider.Facebook: return "Facebook";
      case LinkedSocialProvider.Apple:    return "Apple";
    }
  }

  private createBackground(): void
  {
    const bg_color = "#FFFFFF";
    const bg_corner_radius_design = { tl: 30, tr: 30, br: 70, bl: 70 };
    const circle_w_design = 237;
    const circle_h_design = 234;
    const circle_radius_design = 117;
    const circle_y_design = 136;

    const icon_w_design = 137;
    const icon_h_design = 137;
    const icon_y_design = 46;

    const circle_w = LayoutScaler.widthFromDesign( circle_w_design );
    const circle_h = LayoutScaler.heightFromDesign( circle_h_design );
    const r = LayoutScaler.cornerRadiusFromDesign( circle_radius_design );
    const circle_y = LayoutScaler.yFromDesign( circle_y_design );

    const icon_w = LayoutScaler.widthFromDesign( icon_w_design );
    const icon_h = LayoutScaler.heightFromDesign( icon_h_design );
    const icon_y = LayoutScaler.yFromDesign( icon_y_design );

    this.bg = new RoundedRectangle(
      this.ctx,
      0, 0,
      this.rect.getW(),
      this.rect.getH(),
      {
        tl: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.tl ),
        tr: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.tr ),
        br: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.br ),
        bl: LayoutScaler.cornerRadiusFromDesign( bg_corner_radius_design.bl )
      },
      bg_color
    );
    this.addChild( this.bg );

    const circle_x = this.rect.getW2() - circle_w / 2;

    const circle = new RoundedRectangle(
      this.ctx,
      circle_x,
      -circle_y,
      circle_w,
      circle_h,
      r,
      bg_color
    );
    this.addChild( circle );

    const icon_x = ( circle_w - icon_w ) / 2;

    const main_provider_icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      this.getProviderIcon()
    );
    circle.addChild( main_provider_icon, true );
  }

  private createTitle(): void
  {
    const title_w = this.rect.getW() * 0.95;
    const title_h_design = 68;
    const title_h = LayoutScaler.heightFromDesign( title_h_design );
    const title_x = ( this.rect.getW() - title_w ) / 2;
    const title_y_design = 49;
    const title_y = LayoutScaler.yFromDesign( title_y_design );
    const title_font_size_design = 50;

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "DISCONNECT_ACCOUNT_DIALOG.TITLE",
        text_color: "#DE934F",
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( title_font_size_design ),
        font_weight: 700,
        auto_wrap: false,
        vertical_align: MyTextVerticalAlign.Center, 
        text_variable_params: [this.getProviderDisplayName()],
      }
    );
    this.addChild( title );
  }

  private createContent(): void
  {
    const content_w_design = 948;
    const content_h_design = 469;
    const content_w = LayoutScaler.widthFromDesign( content_w_design );
    const content_h = LayoutScaler.heightFromDesign( content_h_design );
    const content_x = this.rect.getW2() - content_w / 2;
    const content_y_design = 134;
    const content_y = LayoutScaler.yFromDesign( content_y_design );

    this.content_bg = new RoundedRectangle(
      this.ctx,
      content_x,
      content_y,
      content_w,
      content_h,
      0,
      "#FFF3E8"
    );

    const text_w_design = 906;
    const text_h_design = 424;
    const text_w = LayoutScaler.widthFromDesign( text_w_design );
    const text_h = LayoutScaler.heightFromDesign( text_h_design );
    const text_x = ( content_w - text_w ) / 2;
    const text_y = ( content_h - text_h ) / 2;
    const text_font_size_design = 40;

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        localization_key: "DISCONNECT_ACCOUNT_DIALOG.TEXT",
        text_color: "#A85F1E",
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( text_font_size_design ),
        font_weight: 700,
        auto_wrap: true,
        using_y_offset: false,
        vertical_align: MyTextVerticalAlign.Center,
        text_variable_params: [this.getProviderDisplayName()],
      }
    );
    this.content_bg.addChild( text );

    this.bg.addChild( this.content_bg );
  }

  private createActionButtons(): void
  {
    const btn_w_design = 466;
    const btn_h_design = 112;
    const btn_radius_design = 56;
    const gap_design = 23;
    const btn_y_design = 635;
    const btn_font_size_design = 48;

    const btn_w = LayoutScaler.widthFromDesign( btn_w_design );
    const btn_h = LayoutScaler.heightFromDesign( btn_h_design );
    const r = LayoutScaler.cornerRadiusFromDesign( btn_radius_design );
    const gap = LayoutScaler.widthFromDesign( gap_design );
    const btn_y = LayoutScaler.yFromDesign( btn_y_design );

    const row_w = btn_w * 2 + gap;
    const row_x = this.rect.getW2() - row_w / 2;

    const cancel_btn = new RoundedRectangle(
      this.ctx,
      row_x,
      btn_y,
      btn_w,
      btn_h,
      r,
      "#12B2E2",
      undefined,
      true
    );
    cancel_btn.setOnClick( () =>
    {
      DialogManager.closeDialog();
    } );
    cancel_btn.addChild( new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "CLEAR_STATISTICS.NO",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( btn_font_size_design ),
        font_weight: 700,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_light_blue_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    ), true );
    this.addChild( cancel_btn );

    const disconnect_btn = new RoundedRectangle(
      this.ctx,
      row_x + btn_w + gap,
      btn_y,
      btn_w,
      btn_h,
      r,
      "#F28519 ",
      undefined,
      true
    );
    disconnect_btn.setOnClick( () =>
    {
      DialogManager.closeDialog();
      this.on_confirm?.();
    } );
    disconnect_btn.addChild( new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "DISCONNECT",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( btn_font_size_design ),
        font_weight: 700,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_orange_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    ), true );
    this.addChild( disconnect_btn );
  }

  private createCloseIcon(): void
  {
    const icon_size_design = 85;
    const icon_size = LayoutScaler.widthFromDesign( icon_size_design );
    const icon_x = this.rect.getW() - icon_size - LayoutScaler.widthFromDesign( 7 );
    const icon_y = LayoutScaler.yFromDesign( 7 );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    icon.setOnClick( () =>
    {
      DialogManager.closeDialog();
    } );
    this.addChild( icon );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
